import _ from "lodash";
/* external */

/* internal */
import {
  ALPHA,
  DEPENDENT,
  MYP_CRITERIA_TOTAL,
  MYP_FINAL_GRADE,
  MYP_IB_DEFINED,
  DP_IB_DEFINED,
  REGULAR,
  SPECIAL,
  NUMERIC,
} from "Constants/stringConstants";
import { shouldShowLevelsInGradingSetupV2 } from "GradingSetupV2/modules/Utils";
import { getIsGpaCreditsV2Enabled } from "../../../modules/Utils";

/* relative */

// * grade scale actions
export const GRADE_SCALE_PREVIEW = "GRADE_SCALE_PREVIEW";
export const GRADE_SCALE_EDIT = "GRADE_SCALE_EDIT";
export const GRADE_SCALE_RESYNC_IB_GRADE = "GRADE_SCALE_RESYNC_IB_GRADE";
export const GRADE_SCALE_MARK_AS_INACTIVE = "GRADE_SCALE_MARK_AS_INACTIVE";
export const GRADE_SCALE_DELETE = "GRADE_SCALE_DELETE";

const getIsDisabledByGradeScaleType = ({
  t,
  gradeScaleType,
  isUpdatingExistingTemplate,
  state,
  uid,
}) => {
  const scaleTypeLabelMap = {
    [MYP_CRITERIA_TOTAL]: t("academicSetup:ibmyp_0_to_32_scale"),
    [MYP_IB_DEFINED]: t("academicSetup:ibmyp_0_to_8_scale"),
    [MYP_FINAL_GRADE]: t("academicSetup:ibmyp_1_to_7_scale"),
    [DP_IB_DEFINED]: t("academicSetup:ibdp_1_to_7_scale"),
  };

  const options = _.concat(MYP_GRADE_SCALE_OPTIONS, DP_GRADE_SCALE_OPTIONS);
  const isGradeScaleBeingUsed =
    isUpdatingExistingTemplate &&
    !_.includes(["templateName", "templateId"], uid) &&
    (_.get(state, "editData.insights.learningCourseCount") > 0 ||
      _.get(state, "editData.insights.subjectGradeMapCount") > 0);

  const isDisabled =
    _.includes(options, gradeScaleType) || isGradeScaleBeingUsed;

  const getTooltipMessage = () => {
    if (!isDisabled) return null;

    return isGradeScaleBeingUsed
      ? t(
          "academicSetup:cannot_change_this_setting_once_grade_scale_is_applied"
        )
      : t("academicSetup:official_grade_scale_field_disabled_with_label", {
          label: scaleTypeLabelMap[gradeScaleType],
        });
  };
  const tooltip = getTooltipMessage();

  return {
    isDisabled,
    tooltip,
  };
};

export const GRADE_SCALE_CONFIGURATION_TEMPLATE = {
  GRADE_SCALE_TYPES: {
    uid: "gradeScale",
    locale_header: "academicSetup:grade_scale",
    options: [
      {
        uid: "REGULAR",
        locale_header: "common:regular_grade_scale",
        locale_key: "academicSetup:regular_grade_scale_description",
      },
      {
        uid: "SPECIAL",
        locale_header: "common:special_grade_scale",
        locale_key: "academicSetup:special_grade_scale_description",
      },
      {
        uid: "DEPENDENT",
        locale_header: "common:dependent_grade_scale",
        locale_key: "academicSetup:dependent_grade_scale_description",
      },
    ],
  },
  GRADE_SCALE_BASIC_DETAILS: [
    {
      uid: "templateName",
      locale_key: "academicSetup:template_name",
      placeholder: "common:enter_with_label",
      valueType: "text",
      defaultValue: "",
      getIsDisabled: getIsDisabledByGradeScaleType,
    },
    {
      uid: "templateId",
      locale_key: "academicSetup:template_id",
      placeholder: "common:enter_with_label",
      valueType: "text",
      defaultValue: "",
      getIsDisabled: getIsDisabledByGradeScaleType,
    },
    {
      uid: "displayName",
      locale_key: "common:display_name",
      placeholder: "common:enter_with_label",
      valueType: "text",
      defaultValue: "",
    },
    {
      uid: "templateDescription",
      locale_key: "common:description",
      placeholder: "common:enter_with_label_here",
      valueType: "textArea",
      defaultValue: "",
    },
    {
      uid: "type",
      locale_key: "common:type",
      valueType: "typeDropdown",
      defaultValue: "ALPHA",
      options: [
        {
          uid: "ALPHA",
          locale_header: "academicSetup:alpha",
          locale_key: "academicSetup:alpha_grade_type_description",
        },
        {
          uid: "NUMERIC",
          locale_header: "academicSetup:numeric",
          locale_key: "academicSetup:numeric_grade_type_description",
        },
      ],
      getIsDisabled: getIsDisabledByGradeScaleType,
      getIsHidden: ({ gradeScaleType }) => {
        return gradeScaleType === SPECIAL;
      },
    },
    {
      uid: "minValue",
      locale_key: "academicSetup:minimum_value",
      placeholder: "common:enter_with_label",
      valueType: "text",
      defaultValue: "",
      getIsDisabled: getIsDisabledByGradeScaleType,
      getIsHidden: ({ state }) => {
        return _.get(state, "basicDetails.type") !== NUMERIC;
      },
    },
    {
      uid: "maxValue",
      locale_key: "academicSetup:maximum_value",
      placeholder: "common:enter_with_label",
      valueType: "text",
      defaultValue: "",
      getIsDisabled: getIsDisabledByGradeScaleType,
      getIsHidden: ({ state }) => {
        return _.get(state, "basicDetails.type") !== NUMERIC;
      },
    },
    {
      uid: "calculateStandardGradesUsing",
      locale_key: "academicSetup:calculation_method_for_standard_grading_only",
      valueType: "dropdown",
      defaultValue: "NUMERIC_VALUE",
      options: [
        {
          uid: "NUMERIC_VALUE",
          locale_header: "common:numeric_value",
          locale_key: "academicSetup:numeric_value_calculation_description",
        },
        {
          uid: "GRADE_VALUE",
          locale_header: "academicSetup:grade_value",
          locale_key: "academicSetup:grade_value_calculation_description",
        },
      ],
      getIsHidden: ({ state, gradeScaleType, isStandardBasedEnabled }) => {
        return (
          _.get(state, "gradeScaleType") !== REGULAR ||
          _.get(state, "basicDetails.type") === ALPHA ||
          _.includes(
            [...MYP_GRADE_SCALE_OPTIONS, ...DP_GRADE_SCALE_OPTIONS, DEPENDENT],
            gradeScaleType
          ) ||
          !isStandardBasedEnabled
        );
      },
    },
    {
      uid: "isUseForMastery",
      locale_key: "academicSetup:mastery_level_for_standard_grading_only",
      valueType: "dropdown",
      defaultValue: "no",
      options: [
        {
          uid: "yes",
          locale_header: "common:yes",
          locale_key: "academicSetup:mastery_level_yes_description",
        },
        {
          uid: "no",
          locale_header: "common:no",
          locale_key: "academicSetup:mastery_level_no_description",
        },
      ],
      getIsHidden: ({ gradeScaleType, isStandardBasedEnabled }) => {
        return gradeScaleType !== REGULAR || !isStandardBasedEnabled;
      },
    },
    {
      uid: "isUseForCreditGPACalculation",
      locale_key: "academicSetup:use_for_credit_and_gpa_calculation",
      valueType: "dropdown",
      defaultValue: "no",
      options: [
        {
          uid: "yes",
          locale_header: "common:yes",
          locale_key: "academicSetup:credit_gpa_calculation_yes_description",
        },
        {
          uid: "no",
          locale_header: "common:no",
          locale_key: "academicSetup:credit_gpa_calculation_no_description",
        },
      ],
      getIsHidden: ({ gradeScaleType }) => {
        return (
          _.includes([SPECIAL, DEPENDENT], gradeScaleType) ||
          !getIsGpaCreditsV2Enabled()
        );
      },
    },
    {
      uid: "specialGradeTemplate",
      locale_key: "academicSetup:special_grades_template",
      valueType: "dropdown",
      defaultValue: "",
      getIsDisabled: ({ t, isUpdatingExistingTemplate, state }) => {
        const specialGradetemplate = _.get(
          state,
          "editData.specialGradeTemplate",
          null
        );
        const isDisabled =
          isUpdatingExistingTemplate &&
          !_.isEmpty(specialGradetemplate) &&
          (_.get(state, "editData.insights.learningCourseCount") > 0 ||
            _.get(state, "editData.insights.subjectGradeMapCount") > 0);

        return {
          isDisabled,
          tooltip: isDisabled
            ? t(
                "academicSetup:cannot_change_this_setting_once_grade_scale_is_applied"
              )
            : "",
        };
      },
      getIsHidden: ({ gradeScaleType }) => {
        return !_.includes(
          [
            REGULAR,
            DEPENDENT,
            ...MYP_GRADE_SCALE_OPTIONS,
            ...DP_GRADE_SCALE_OPTIONS,
          ],
          gradeScaleType
        );
      },
    },
    {
      uid: "differentGradebookValuesForLevels",
      locale_key: "academicSetup:define_gradebook_values_for_each_level",
      valueType: "dropdown",
      defaultValue: "no",
      options: [
        {
          uid: "yes",
          locale_header: "common:yes",
        },
        {
          uid: "no",
          locale_header: "common:no",
        },
      ],
      getIsDisabled: ({ t, isUpdatingExistingTemplate, state }) => {
        const isDisabled =
          isUpdatingExistingTemplate &&
          _.get(state, "editData.metadata.differentGradebookValuesForLevels") &&
          (_.get(state, "editData.insights.learningCourseCount") > 0 ||
            _.get(state, "editData.insights.subjectGradeMapCount") > 0);

        return {
          isDisabled,
          tooltip: isDisabled
            ? t(
                "academicSetup:cannot_change_this_setting_once_grade_scale_is_applied"
              )
            : "",
        };
      },
      getIsHidden: ({ gradeScaleType, curriculumId, levelsData }) => {
        return (
          !shouldShowLevelsInGradingSetupV2(curriculumId) ||
          !_.includes([REGULAR, DP_IB_DEFINED], gradeScaleType) ||
          _.isEmpty(levelsData)
        );
      },
    },
    // Removed levels selection dropdown — levels are now auto-populated
    {
      uid: "gradeScalesToConvert",
      locale_key: "academicSetup:select_grade_scales_to_convert_from",
      valueType: "dropdown",
      defaultValue: "",
      getIsDisabled: ({ t, isUpdatingExistingTemplate, state }) => {
        const isDisabled =
          isUpdatingExistingTemplate &&
          (_.get(state, "editData.insights.learningCourseCount") > 0 ||
            _.get(state, "editData.insights.subjectGradeMapCount") > 0);

        return {
          isDisabled,
          tooltip: isDisabled
            ? t(
                "academicSetup:cannot_remove_this_grade_scale_once_dependent_gradescale_is_applied"
              )
            : "",
        };
      },
      getIsHidden: ({ gradeScaleType }) => {
        return gradeScaleType !== DEPENDENT;
      },
    },
  ],
  GRADE_SCALE_TABLE: [
    {
      uid: "gradeScaleTable",
      locale_key: "academicSetup:grade_scale",
    },
  ],
  // temporarily hide bulk apply options
  // BULK_APPLY_TO_COURSES: [
  //   {
  //     uid: "bulkApplyToCourses",
  //     locale_key: "Bulk apply to courses",
  //   },
  // ],
  // BULK_APPLY_TO_SUBJECTS: [
  //   {
  //     uid: "bulkApplyToSubjects",
  //     locale_key: "Bulk apply to subjects",
  //   },
  // ],
};

export const GRADE_SCALE_TYPE_LABEL = {
  [REGULAR]: "common:regular_grade_scale",
  [SPECIAL]: "academicSetup:special_codes_grade_scale",
  [DEPENDENT]: "common:dependent_grade_scale",
  [MYP_FINAL_GRADE]: "common:regular_grade_scale",
  [MYP_IB_DEFINED]: "common:regular_grade_scale",
  [MYP_CRITERIA_TOTAL]: "common:regular_grade_scale",
  [DP_IB_DEFINED]: "common:regular_grade_scale",
};

export const MYP_GRADE_SCALE_OPTIONS = [
  MYP_FINAL_GRADE,
  MYP_IB_DEFINED,
  MYP_CRITERIA_TOTAL,
];

export const DP_GRADE_SCALE_OPTIONS = [DP_IB_DEFINED];
